import { FC, MouseEvent, useRef, useState } from 'react'

import { Box } from '@factorialco/deprecated-design-system'
import {
  GroupDetailInfoDocument,
  PeopleChartGroupFragment,
  PeopleChartTeamSelectorFragment,
  PeopleChartTeamsDocument,
} from 'generated/resources/graphql'
import keyBy from 'lodash/keyBy'

import { track, trackingEvents } from 'modules/core/lib/Tracking'
import { handleResult } from 'modules/core/lib/handleMutation'
import i18n from 'modules/core/lib/i18n'
import app from 'modules/core/store/models/app'

import ActionBar from 'modules/people_chart/components/ActionBar'
import { SearchItem } from 'modules/people_chart/components/ActionBar/SearchBar/SearchBarItem'
import useTree from 'modules/people_chart/hooks/useTree'
import useZoom from 'modules/people_chart/hooks/useZoom'
import type { Coordinates } from 'modules/people_chart/lib/Coordinates'
import { Dimensions } from 'modules/people_chart/lib/Dimensions'
import hierarchyLib from 'modules/people_chart/lib/Hierarchy'
import { HierarchyLink } from 'modules/people_chart/lib/Hierarchy'
import type { PointNode } from 'modules/people_chart/lib/PointNode'

import TeamSelector from '../TeamSelector'
import TeamSelectorWrapper from '../TeamSelector/Wrapper'
import { usePeopleTeamCreateTeam } from '../TeamSelector/usePeopleTeamCreateTeam'
import ZoomControls from '../ZoomControls'
import Frame from './Frame'
import Node, {
  GROUP_NODE_SIZE_COLLAPSED,
  GROUP_NODE_SIZE_COLLAPSED_NO_MEMBERS,
  NODE_SIZE,
} from './Node'

export interface Props {
  size: Dimensions
  teams: (PeopleChartGroupFragment & PeopleChartTeamSelectorFragment)[]
  hierarchy: HierarchyLink[]
  canEdit: boolean
  editing: boolean
  activeTeamId: string
  upsert: (hierarchyJson: string) => Promise<void>
  onChartEditing: (editing: boolean) => void
  onTeamSelection: (id: number, openSidepanel: boolean) => void
  onEmployeeSelection: (id: number) => void
  setOpenTeam: (teamId: number) => void
  setSidePanelEditing: (value: boolean) => void
}

const GroupChart: FC<Props> = ({
  teams,
  hierarchy,
  size,
  canEdit,
  editing,
  onChartEditing,
  onTeamSelection,
  onEmployeeSelection,
  upsert,
  activeTeamId,
  setOpenTeam,
  setSidePanelEditing,
}) => {
  const hasHierarchy = hierarchy.length > 1
  const svgRef = useRef<SVGSVGElement>(null)
  const [width, height] = size
  const [openSelector, setOpenSelector] = useState(false)
  const [selectedTeamNode, setSelectedTeamNode] = useState<string | null>(null)
  const disableZoom = !hasHierarchy || openSelector
  const zoom = useZoom(svgRef, size, disableZoom, disableZoom)
  const teamsHierarchy = hierarchyLib.identifiers('team', hierarchy)

  const availableTeams = teams.filter((team) => !teamsHierarchy.has(team.id.toString()))
  const indexedGroups = keyBy(teams, 'id')
  const teamIds = Object.keys(indexedGroups)
  const { tree, links, treeBoundary, findFirstNode } = useTree(hierarchy, width, teamIds)

  const [modalPosition, setModalPosition] = useState({ top: '0', left: '0' })
  const modalRef = useRef<HTMLDivElement>(null)
  const automaticTeamName = i18n.t('people_chart.new_team_name')

  const nextTeamNumber =
    teams.reduce((acc, team) => {
      const posibleNumber = Number(team.name.replace(automaticTeamName, ''))
      if (team.name.startsWith(automaticTeamName) && !isNaN(posibleNumber)) {
        return posibleNumber > acc ? posibleNumber : acc
      }
      return acc
    }, 0) + 1

  const treeLinks = links
    .filter((link) => link.source.data.type !== 'organization')
    .map((link, i) => {
      const sourceTeamMembers = link.source.id
        ? indexedGroups[link.source.id]?.firstMembers.nodes || []
        : []
      const sourceTeamHasMembers = sourceTeamMembers.length > 0
      const nodeHeight = sourceTeamHasMembers
        ? GROUP_NODE_SIZE_COLLAPSED[1]
        : GROUP_NODE_SIZE_COLLAPSED_NO_MEMBERS[1]
      const sourceCoordinates: Coordinates = [link.source.x, link.source.y]
      const targetCoordinates: Coordinates = [link.target.x, link.target.y]
      const dimensions: Dimensions = [NODE_SIZE[0], nodeHeight]
      const sourceProps = { coordinates: sourceCoordinates, dimensions: dimensions }
      const targetProps = { coordinates: targetCoordinates, dimensions: dimensions }

      return (
        <Node.Link
          key={`${i}-link-${link.source.id}-to-${link.target.id}`}
          source={sourceProps}
          target={targetProps}
        />
      )
    })

  const handleGroupNodeClick = (id: number) => {
    onTeamSelection(id, true)
    track(trackingEvents.teamChart.goToTeamSidepanel)
  }

  const groupNode = (node: PointNode<HierarchyLink>, group: PeopleChartGroupFragment) => {
    const isActive = node.id === activeTeamId

    return (
      <Node.Group
        key={`group-node-${node.id}`}
        node={node}
        size={NODE_SIZE}
        group={group}
        editing={editing}
        onGroupClick={handleGroupNodeClick}
        onNodeAdded={setSelectedTeamNode}
        setOpenSelector={setOpenSelector}
        setModalPosition={setModalPosition}
        active={isActive}
      />
    )
  }

  const treeNodes = tree.map((node) => {
    // don't render the parent is a synthetic node
    if (node.parent == null) {
      return
    }

    const group = node.id ? indexedGroups[node.id] : null
    if (group) {
      return groupNode(node, group)
    }

    return null
  })

  const handleContainerClick = (event: MouseEvent) => {
    if (modalRef.current && !modalRef.current.contains(event.target as Node)) {
      setOpenSelector(false)
    }
  }

  const trackAddTeam = () => {
    track(trackingEvents.teamChart.addExistingTeamFromChart)
  }

  const handleTeamSelection = (teamId: string, newTeam = false) => {
    setOpenSelector(false)
    if (newTeam) {
      onTeamSelection(Number(teamId), true)
      setOpenTeam(Number(teamId))
      setSidePanelEditing(true)
    }

    const result = hierarchyLib.addNode(
      { parentId: selectedTeamNode, id: teamId, type: 'team' },
      hierarchy
    )
    trackAddTeam()
    return upsert(hierarchyLib.toJSON(result))
  }

  const handleInitialTeamSelection = (teamId: string, newTeam = false) => {
    if (newTeam) {
      onTeamSelection(Number(teamId), true)
      setOpenTeam(Number(teamId))
      setSidePanelEditing(true)
    }
    setOpenSelector(false)
    const initializeHierarchy = hierarchyLib.initialize()
    const result = hierarchyLib.addNode(
      { parentId: '0', id: teamId, type: 'team' },
      initializeHierarchy
    )
    onChartEditing(true)
    trackAddTeam()
    return upsert(hierarchyLib.toJSON(result))
  }

  const { loading: creating, create: createTeam } = usePeopleTeamCreateTeam()
  const create = async (initialTeam = false) => {
    const name = `${automaticTeamName} ${nextTeamNumber}`
    const result = await createTeam({
      variables: {
        name: name,
      },
      awaitRefetchQueries: true,
      refetchQueries: [
        {
          query: PeopleChartTeamsDocument,
        },
        {
          query: GroupDetailInfoDocument,
        },
      ],
    })
    const handled = handleResult(result, () => undefined)
    if (handled.success) {
      const teamId = String(handled.data.team?.id)
      if (teamId) {
        initialTeam ? handleInitialTeamSelection(teamId, true) : handleTeamSelection(teamId, true)
      }
    } else {
      app.setFlash({
        type: 'wrong',
        message: 'error',
      })
    }
  }

  const handleZoom = (value: number) => {
    zoom.scale(value)
  }

  const handleZoomToFit = () => {
    zoom.fitToBoundingBox(treeBoundary)
  }

  const handleSearchTeamSelection = (teamId: string, openSidepanel = true) => {
    const node = tree.find((n) => String(n.id) === String(teamId))
    if (node) {
      zoom.fitToBoundingBox({
        x0: node.x,
        x1: node.x + GROUP_NODE_SIZE_COLLAPSED[0],
        y0: node.y,
        y1: node.y + GROUP_NODE_SIZE_COLLAPSED[1],
      })
      onTeamSelection(Number(teamId), openSidepanel)
    }
  }

  const handleSearchEmployeeSelection = (employeeId: string, employeeTeamIds: string[]) => {
    const firstTeam = findFirstNode(employeeTeamIds)

    if (firstTeam) {
      handleSearchTeamSelection(firstTeam, false)
    }
    onEmployeeSelection(Number(employeeId))
  }

  const handleSearchItemSelection = (item: SearchItem, ids: string[]) => {
    track(trackingEvents.teamChart.goToTeamChartFilterResult)

    if (item.type === 'team') {
      handleSearchTeamSelection(String(item.id))
    }

    if (item.type === 'employee') {
      handleSearchEmployeeSelection(String(item.id), ids)
    }
  }

  const handleEditClick = () => {
    track(trackingEvents.teamChart.editTeamChart)
    onChartEditing(!editing)
  }

  return (
    <>
      <ActionBar
        canEdit={canEdit}
        editing={editing}
        disabled={!hasHierarchy}
        onEditingToggle={handleEditClick}
        onSearchSelection={handleSearchItemSelection}
      />
      {/* eslint-disable-next-line @typescript-eslint/no-explicit-any */}
      <Box onClick={(ev: any) => handleContainerClick(ev)}>
        {!hasHierarchy && (
          <TeamSelectorWrapper
            onChange={handleInitialTeamSelection}
            onAddTeamClicked={() => create(true)}
            teams={availableTeams}
          />
        )}
        <Frame
          ref={svgRef}
          width={width}
          height={height}
          transform={zoom.value}
          showPointer={!openSelector}
        >
          <>
            <g>{treeLinks}</g>
            <g>{treeNodes}</g>
          </>
        </Frame>

        {hasHierarchy && (
          <ZoomControls
            onClick={handleZoom}
            onZoomToFit={handleZoomToFit}
            disabled={{
              zoomIn: zoom.isZoomMaxed || disableZoom,
              zoomOut: disableZoom,
              zoomToFit: disableZoom,
            }}
          />
        )}
        {openSelector && (
          <Box
            ref={modalRef}
            style={{
              position: 'fixed',
              top: `${modalPosition.top}px`,
              left: `${modalPosition.left}px`,
            }}
          >
            <TeamSelector
              width={'s400'}
              onChange={handleTeamSelection}
              onAddTeamClicked={() => create()}
              teams={availableTeams}
            />
          </Box>
        )}
      </Box>
    </>
  )
}

export default GroupChart
